import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import {
  Loader2, TrendingUp, DollarSign, Users, CheckCircle, Clock, MapPin,
  FileText, X, MessageCircle, Send, Home, Ruler, Calendar, Layers,
  BarChart3, ExternalLink, Download, Box, ShoppingBag, Eye, Lock, Unlock,
  ChevronDown, ChevronUp, ChevronRight, Plus, Shield, Briefcase, Zap, User, Check,
  ArrowUpRight, ArrowDownRight, Bookmark, Ban, Target, HelpCircle, Edit3
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Card, GoldButton, inputClassLight as inputClass, labelClassLight as labelClass } from "../components/shared/UIComponents";
import { fmtUSD, fmtUSDCompact } from "../utils/currency";
import { formatAddress, estimatedRiskTier, cityStateOnly } from "../utils/addressFormat";
import { FilterPills, LivePulse, SocialProofBadge } from "../components/shared/QuickActions";
import { downloadReport } from "../utils/downloadReport";
import { stageLabel, stageBadgeColor } from "../lib/stageConstants";
import { toast } from "sonner";
import DealDetailModal from "../components/DealDetailModal";
import { useTabNav } from "../hooks/useTabNav";
import DealRoomChat from "../components/DealRoomChat";
import LoanBoxManager from "../components/LoanBoxManager";
import MarketplaceDeals from "../components/MarketplaceDeals";
import EntitlementBanner from "../components/shared/EntitlementBanner";
import LenderMetricsPanel from "../components/lender/LenderMetricsPanel";
import EntityComms from "../components/shared/EntityComms";
import { LenderServicingPanel } from "../components/shared/ServicingPanel";
import { useMyRequestCounts } from "../hooks/useCommCounts";
import usePullToRefresh from "../hooks/usePullToRefresh";
import ProfileEditor from "../components/shared/ProfileEditor";
import { DealComparison } from "../components/shared/DealComparison";
import { usePageNavigate } from "../hooks/usePageNavigate";
import PrepaymentPenaltyInput, { structureToString } from "../components/shared/PrepaymentPenaltyInput";
import { MapThumbnail, MetricPill, StatusBadge, ScoreBadge } from "../components/shared/DesignSystem";
import ClosingChecklist from "../components/ClosingChecklist";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const loanTypeLabels = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial" };

const lenderStatusColors = {
  received: "bg-gray-100 text-gray-600",
  reviewing: "bg-blue-100 text-blue-700",
  interested: "bg-amber-100 text-amber-700",
  accepted: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-600",
  requested_info: "bg-amber-100 text-amber-700",
  terms_issued: "bg-violet-100 text-violet-700",
  selected: "bg-emerald-200 text-emerald-800",
  passed: "bg-red-100 text-red-600",
  closed: "bg-gray-200 text-gray-700",
};
const lenderStatusLabels = {
  received: "Received",
  reviewing: "Under Review",
  interested: "Interested",
  accepted: "Accepted",
  rejected: "Declined",
  requested_info: "Info Requested",
  terms_issued: "Terms Issued",
  selected: "Selected",
  passed: "Passed",
  closed: "Closed",
};

export default function LenderPortal() {
  const { user } = useAuth();
  const { navigateTo, navigateToEntity } = usePageNavigate();
  const { openCount: commOpenCount } = useMyRequestCounts();
  const { data, isLoading: loading, refetch: refetchDeals } = useQuery({
    queryKey: ["lender-portal-deals"],
    queryFn: () => axios.get(`${API}/portal/lender/deals`).then(r => r.data),
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });
  const [expressing, setExpressing] = useState(null);
  const [tsModal, setTsModal] = useState(null); // lead_id for term sheet modal
  const [tsForm, setTsForm] = useState({ interest_rate: "12.5", points: "2.0", loan_amount: "", term_months: "12", closing_days: "10", payment_structure: "interest_only", prepayment_penalty_structure: { type: "none" }, conditions: "", notes: "" });
  const [tsSubmitting, setTsSubmitting] = useState(false);
  const [tsError, setTsError] = useState(null);
  const [mySheets, setMySheets] = useState([]);
  const [dealRoom, setDealRoom] = useState(null); // lead_id for deal room
  const [roomMessages, setRoomMessages] = useState([]);
  const [roomMsg, setRoomMsg] = useState("");
  const [roomSending, setRoomSending] = useState(false);
  const roomRef = React.useRef(null);
  const [propertyModal, setPropertyModal] = useState(null); // deal object
  const [propertyData, setPropertyData] = useState(null);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [dealFilter, setDealFilter] = useState("all");

  const DEAL_FILTERS = [
    { id: "all", label: "All" },
    { id: "interested", label: "Interested" },
    { id: "quoted", label: "Quoted" },
    { id: "selected", label: "Selected" },
    { id: "active", label: "Active" },
    { id: "declined", label: "Declined" },
  ];

  const [detailLeadId, setDetailLeadId] = useState(null);
  const [allNotifs, setAllNotifs] = useState([]);
  const [feedCollapsed, setFeedCollapsed] = useState(false);
  const [selectedDeals, setSelectedDeals] = useState(new Set());
  const [showDealComparison, setShowDealComparison] = useState(false);
  const [dashSection, setDashSection] = useTabNav("section", "deals");

  // Quick term sheet modal
  const [quickTsModal, setQuickTsModal] = useState(null); // lead_id
  const [quickTsForm, setQuickTsForm] = useState({ rate: "12.5", ltv: "", term_months: "12", closing_days: "10" });
  const [quickTsSubmitting, setQuickTsSubmitting] = useState(false);

  // Pass/Watchlist state
  const [passingDeal, setPassingDeal] = useState(null);
  const [savingDeal, setSavingDeal] = useState(null);
  const [passedDeals, setPassedDeals] = useState(new Set());
  const [viewedIds, setViewedIds] = useState(new Set());

  // Lender Terms Modal state (Part 1 - Governance)
  const [lenderTermsAccepted, setLenderTermsAccepted] = useState(null); // null = loading, true/false
  const [termsAccepting, setTermsAccepting] = useState(false);
  const [termsScrolledToBottom, setTermsScrolledToBottom] = useState(false);
  const termsContentRef = React.useRef(null);

  useEffect(() => {
    axios.get(`${API}/lender/terms-status`).then(r => {
      setLenderTermsAccepted(r.data?.lender_terms_accepted || false);
    }).catch(() => setLenderTermsAccepted(false));
  }, []);

  const handleAcceptTerms = async () => {
    setTermsAccepting(true);
    try {
      await axios.post(`${API}/lender/accept-platform-terms`, { accepted: true });
      setLenderTermsAccepted(true);
      toast.success("Platform agreement accepted. Welcome to Imaani Capital.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept terms");
    } finally {
      setTermsAccepting(false);
    }
  };

  const handleTermsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 40) {
      setTermsScrolledToBottom(true);
    }
  };

  const fetchViewedIds = () => {
    axios.get(`${API}/lender/deals/viewed-ids`).then(r => setViewedIds(new Set(r.data?.viewed_ids || []))).catch(() => {});
  };

  const trackDealView = (leadId) => {
    axios.post(`${API}/lender/deals/${leadId}/view`).catch(() => {});
    setViewedIds(prev => new Set([...prev, leadId]));
  };

  const fetchNotifications = () => {
    axios.get(`${API}/notifications`).then(r => {
      setAllNotifs(r.data?.notifications || r.data || []);
    }).catch(() => {});
  };

  const fetchMySheets = () => {
    axios.get(`${API}/portal/lender/my-term-sheets`).then(r => setMySheets(r.data.term_sheets || [])).catch(() => {});
  };
  useEffect(() => { fetchMySheets(); fetchNotifications(); fetchViewedIds(); const nInterval = setInterval(fetchNotifications, 20000); return () => clearInterval(nInterval); }, []);

  const { pullIndicator } = usePullToRefresh(() => Promise.all([refetchDeals(), fetchMySheets()]));

  const openDealRoom = (leadId) => {
    setDealRoom(leadId);
    axios.get(`${API}/deals/${leadId}/messages`).then(r => setRoomMessages(r.data.messages || [])).catch(() => {});
  };

  useEffect(() => {
    if (!dealRoom) return;
    const interval = setInterval(() => {
      axios.get(`${API}/deals/${dealRoom}/messages`).then(r => setRoomMessages(r.data.messages || [])).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [dealRoom]);

  useEffect(() => { roomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [roomMessages]);

  const sendRoomMsg = async (e) => {
    e.preventDefault();
    if (!roomMsg.trim()) return;
    setRoomSending(true);
    try {
      await axios.post(`${API}/deals/${dealRoom}/messages`, { content: roomMsg.trim() });
      setRoomMsg("");
      const r = await axios.get(`${API}/deals/${dealRoom}/messages`);
      setRoomMessages(r.data.messages || []);
    } catch (err) { console.error(err); }
    finally { setRoomSending(false); }
  };

  const expressInterest = async (leadId) => {
    setExpressing(leadId);
    try {
      await axios.post(`${API}/portal/lender/deals/${leadId}/interest`, { message: "Interested in this deal" });
      toast.success("Interest expressed successfully");
      fetchDeals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to express interest");
    }
    finally { setExpressing(null); }
  };

  const openTermSheet = (deal) => {
    setTsForm(f => ({ ...f, loan_amount: String(deal.requested_loan_amount || deal.purchase_price || "") }));
    setTsModal(deal.lead_id);
    setTsError(null);
  };

  const submitTermSheet = async (e) => {
    e.preventDefault();
    setTsSubmitting(true);
    setTsError(null);
    try {
      await axios.post(`${API}/portal/lender/deals/${tsModal}/term-sheet`, {
        interest_rate: parseFloat(tsForm.interest_rate),
        points: parseFloat(tsForm.points),
        loan_amount: parseFloat(tsForm.loan_amount),
        term_months: parseInt(tsForm.term_months),
        closing_days: parseInt(tsForm.closing_days),
        payment_structure: tsForm.payment_structure,
        prepayment_penalty_structure: tsForm.prepayment_penalty_structure,
        conditions: tsForm.conditions,
        notes: tsForm.notes,
      });
      toast.success("Term sheet submitted successfully");
      setTsModal(null);
      fetchDeals();
      fetchMySheets();
    } catch (err) {
      setTsError(err.response?.data?.detail || "Submission failed");
    } finally { setTsSubmitting(false); }
  };

  const hasTermSheet = (leadId) => mySheets.some(s => s.lead_id === leadId && s.status === "submitted");

  // Quick term sheet submit
  const submitQuickTs = async (e) => {
    e.preventDefault();
    setQuickTsSubmitting(true);
    try {
      await axios.post(`${API}/lender/quick-term-sheet/${quickTsModal}`, {
        rate: parseFloat(quickTsForm.rate),
        ltv: quickTsForm.ltv ? parseFloat(quickTsForm.ltv) : null,
        term_months: parseInt(quickTsForm.term_months),
        closing_days: parseInt(quickTsForm.closing_days),
      });
      toast.success("Term sheet submitted!");
      setQuickTsModal(null);
      refetchDeals();
      fetchMySheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setQuickTsSubmitting(false); }
  };

  // Pass on deal
  const handlePassDeal = async (leadId, reason = "other") => {
    setPassingDeal(leadId);
    try {
      await axios.post(`${API}/lender/pass/${leadId}`, { reason });
      toast.success("Deal passed");
      setPassedDeals(prev => new Set([...prev, leadId]));
      refetchDeals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to pass");
    } finally { setPassingDeal(null); }
  };

  // Save to watchlist
  const handleWatchlist = async (leadId) => {
    setSavingDeal(leadId);
    try {
      await axios.post(`${API}/lender/watchlist/${leadId}`);
      toast.success("Saved to watchlist");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSavingDeal(null); }
  };

  const openProperty = async (deal) => {
    setPropertyModal(deal);
    setPropertyData(null);
    setPropertyLoading(true);
    try {
      const res = await axios.get(`${API}/deals/${deal.lead_id}/property`);
      setPropertyData(res.data);
    } catch (err) { console.error(err); }
    finally { setPropertyLoading(false); }
  };

  // Derive all data before early return (hooks must be unconditional)
  const deals = (data?.deals || []).filter(d => !passedDeals.has(d.lead_id));
  const filteredDeals = dealFilter === "all" ? deals : deals.filter(d => {
    if (dealFilter === "interested") return d.has_expressed_interest;
    if (dealFilter === "quoted") return hasTermSheet?.(d.lead_id);
    if (dealFilter === "selected") return d.selected_lender_name && d.selected_lender_name === user?.full_name;
    if (dealFilter === "active") return ["funding", "funded"].includes(d.stage);
    if (dealFilter === "declined") return d.lender_status === "rejected";
    return true;
  });
  const prequalCount = deals.filter(d => d.summary?.decision === "prequalify").length;
  const totalVolume = deals.reduce((s, d) => s + (d.requested_loan_amount || d.purchase_price || 0), 0);
  const submittedSheets = mySheets.filter(s => s.status === "submitted").length;
  const selectedSheets = mySheets.filter(s => s.status === "selected").length;
  const awaitingCount = deals.filter(d => !hasTermSheet(d.lead_id) && d.summary?.decision === "prequalify").length;

  const feedItems = useMemo(() => {
    const items = [];
    filteredDeals.forEach(deal => {
      items.push({ type: "deal", deal, ts: deal.updated_at || deal.created_at || "" });
    });
    allNotifs.slice(0, 15).forEach(n => {
      items.push({ type: "activity", notif: n, ts: n.created_at || "" });
    });
    items.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
    return items;
  }, [filteredDeals, allNotifs]);

  if (loading) return (
    <div className="min-h-[60vh] bg-[#FBF8F1] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
  );

  const NAV_ITEMS = [
    { id: "deals", label: "Deal Flow", icon: TrendingUp },
    { id: "pipeline", label: "Pipeline", icon: Target },
    { id: "marketplace", label: "Marketplace", icon: ShoppingBag },
    { id: "loan_boxes", label: "My Loan Boxes", icon: Box },
    { id: "term_sheets", label: "Term Sheets", icon: FileText },
    { id: "performance", label: "My Stats", icon: BarChart3 },
    { id: "servicing", label: "Servicing", icon: DollarSign },
    { id: "messages", label: "Messages", icon: MessageCircle },
    { id: "profile", label: "My Profile", icon: User },
  ];

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="lender-portal">
      {pullIndicator}

      {/* ══ PART 1: LENDER PLATFORM AGREEMENT MODAL ════════════════ */}
      {lenderTermsAccepted === false && (
        <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="lender-terms-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-[#E5DFD1]">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1B2B1A] to-[#2D3B2C] px-6 py-5 flex items-center gap-3 shrink-0">
              <div className="w-10 h-10 rounded-full bg-[#e5c98b]/20 flex items-center justify-center">
                <FileText className="h-5 w-5 text-[#e5c98b]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white tracking-tight">Imaani Capital — Lender Platform Agreement</h2>
                <p className="text-xs text-white/60 mt-0.5">Please read and accept before accessing the platform</p>
              </div>
            </div>

            {/* Scrollable Terms Content */}
            <div
              ref={termsContentRef}
              onScroll={handleTermsScroll}
              className="flex-1 overflow-y-auto px-6 py-5 text-sm leading-relaxed text-[#3a3a3a] space-y-4"
              style={{ maxHeight: "55vh" }}
            >
              <p className="text-[#666] text-xs">By accessing deal information and submitting term sheets on the Imaani Capital platform, you agree to the following:</p>

              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-[#F9F6F0] border border-[#EDE8DC]">
                  <h4 className="font-semibold text-[#1B2B1A] mb-1">1. Confidentiality</h4>
                  <p className="text-xs text-[#555]">All borrower information, deal data, financial details, and property information accessed through this platform is strictly confidential. You agree not to share, distribute, or use this information for any purpose other than evaluating and funding deals through Imaani Capital.</p>
                </div>

                <div className="p-3 rounded-lg bg-[#F9F6F0] border border-[#EDE8DC]">
                  <h4 className="font-semibold text-[#1B2B1A] mb-1">2. Binding Term Sheets</h4>
                  <p className="text-xs text-[#555]">Term sheets submitted through the platform constitute binding lending offers subject to borrower selection and Imaani Capital approval. Submitting a term sheet confirms your intent and ability to fund the stated loan amount under the stated terms.</p>
                </div>

                <div className="p-3 rounded-lg bg-[#F9F6F0] border border-[#EDE8DC]">
                  <h4 className="font-semibold text-[#1B2B1A] mb-1">3. Fee Acknowledgment</h4>
                  <p className="text-xs text-[#555]">Imaani Capital earns an origination fee and platform fee on all funded deals. These fees are disclosed on each deal and do not reduce the lender's stated return.</p>
                </div>

                <div className="p-3 rounded-lg bg-[#F9F6F0] border border-[#EDE8DC]">
                  <h4 className="font-semibold text-[#1B2B1A] mb-1">4. No Guarantee</h4>
                  <p className="text-xs text-[#555]">Imaani Capital acts as an intermediary only. We do not guarantee borrower performance, property values, or deal outcomes. All lending decisions are made at your sole discretion and risk.</p>
                </div>

                <div className="p-3 rounded-lg bg-[#F9F6F0] border border-[#EDE8DC]">
                  <h4 className="font-semibold text-[#1B2B1A] mb-1">5. Accredited Status</h4>
                  <p className="text-xs text-[#555]">You confirm you are a sophisticated lender with experience in private real estate lending and are authorized to make lending decisions on behalf of yourself or your organization.</p>
                </div>

                <div className="p-3 rounded-lg bg-[#F9F6F0] border border-[#EDE8DC]">
                  <h4 className="font-semibold text-[#1B2B1A] mb-1">6. Governing Law</h4>
                  <p className="text-xs text-[#555]">This agreement is governed by the laws of the State of Texas. Any disputes will be resolved by binding arbitration in Harris County, Texas.</p>
                </div>
              </div>

              <p className="text-[10px] text-[#999] italic mt-4 pt-3 border-t border-[#EDE8DC]">This agreement was prepared as a starting point and should be reviewed by a qualified attorney before use with real lenders.</p>
            </div>

            {/* Footer with Accept */}
            <div className="px-6 py-4 border-t border-[#EDE8DC] bg-[#FDFCF9] shrink-0">
              {!termsScrolledToBottom ? (
                <p className="text-xs text-[#999] text-center">Please scroll through the entire agreement to continue</p>
              ) : (
                <button
                  data-testid="accept-lender-terms-btn"
                  onClick={handleAcceptTerms}
                  disabled={termsAccepting}
                  className="w-full py-3 rounded-xl bg-[#1B2B1A] text-white font-semibold text-sm hover:bg-[#2D3B2C] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {termsAccepting ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Accepting...</>
                  ) : (
                    <><CheckCircle className="h-4 w-4" /> I Accept the Imaani Capital Platform Agreement</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── BLOOMBERG TICKER BAR ─────────────────────────── */}
      <div className="bg-[#F5F0E8] border-b border-[#EDE8DC]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6 py-2.5 overflow-x-auto scrollbar-hide">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#065f46] whitespace-nowrap">Lender Network</span>
            <div className="h-3 w-px bg-[#D4CFC4]" />
            {[
              { label: "DEALS", value: deals.length },
              { label: "PRE-QUAL", value: prequalCount, color: "text-emerald-700", tooltip: "Deals that have passed initial screening and are ready for your review." },
              { label: "VOLUME", value: `$${(totalVolume / 1e6).toFixed(1)}M`, color: "text-[#8B7340]" },
              { label: "MY TERMS", value: submittedSheets },
              { label: "SELECTED", value: selectedSheets, color: selectedSheets > 0 ? "text-emerald-700" : "" },
              { label: "AWAITING", value: awaitingCount },
            ].map((t, i) => (
              <div key={i} className="flex items-center gap-2 whitespace-nowrap group relative">
                <span className="text-[9px] font-medium text-[#999] uppercase tracking-wider flex items-center gap-1">
                  {t.label}
                  {t.tooltip && <HelpCircle className="h-3 w-3 text-[#bbb] hover:text-emerald-600 cursor-help" />}
                </span>
                <span className={`text-sm font-mono font-bold ${t.color || "text-[#0A0A0A]"}`}>{t.value}</span>
                {t.tooltip && (
                  <span className="absolute bottom-full left-0 mb-2 w-52 p-2 rounded-lg bg-[#0A0A0A] text-white text-[10px] leading-relaxed shadow-xl z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                    {t.tooltip}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 3-COLUMN FEED LAYOUT ─────────────────────────── */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] xl:grid-cols-[260px_1fr_280px] gap-6">

          {/* ── LEFT SIDEBAR: Profile + Nav ── */}
          <div className="hidden lg:block">
            <div className="sticky top-6 space-y-4">
              {/* Profile Card */}
              <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                <div className="h-16 bg-[#064e3b] relative">
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(229,201,139,0.15),transparent_60%)]" />
                </div>
                <div className="px-4 pb-4 -mt-6 relative z-10">
                  <div className="w-12 h-12 rounded-full bg-emerald-600 border-2 border-white flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {(user?.full_name || "L")[0]}
                  </div>
                  <h3 className="text-sm font-bold text-[#0A0A0A] mt-2 tracking-tight">{user?.full_name}</h3>
                  <p className="text-[10px] text-[#888] mt-0.5">
                    {user?.approval_status === "approved" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                        <Shield className="h-3 w-3" /> Verified Lender
                      </span>
                    ) : (
                      <span className="text-amber-600 font-medium">Pending Verification</span>
                    )}
                  </p>
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-[#EDE8DC]">
                    <div className="text-center">
                      <div className="text-base font-bold text-[#0A0A0A] tabular-nums">{deals.length}</div>
                      <div className="text-[9px] text-[#999] uppercase tracking-wider">Deals</div>
                    </div>
                    <div className="text-center">
                      <div className="text-base font-bold text-emerald-600 tabular-nums">{submittedSheets}</div>
                      <div className="text-[9px] text-[#999] uppercase tracking-wider">Quoted</div>
                    </div>
                    <div className="text-center">
                      <div className="text-base font-bold text-[#E5C98B] tabular-nums">{selectedSheets}</div>
                      <div className="text-[9px] text-[#999] uppercase tracking-wider">Selected</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Nav */}
              <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                <div className="p-1">
                  {NAV_ITEMS.map(s => (
                    <button key={s.id} onClick={() => setDashSection(s.id)}
                      data-testid={`lender-nav-${s.id}`}
                      className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                        dashSection === s.id ? "bg-emerald-700 text-white" : "text-[#666] hover:text-[#333] hover:bg-[#FDFCF9]"
                      }`}>
                      <s.icon className="h-3.5 w-3.5" />
                      {s.label}
                      {s.id === "messages" && commOpenCount > 0 && (
                        <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-100 text-red-600">{commOpenCount}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── CENTER: The Feed ── */}
          <div className="min-w-0">
            {/* Mobile-only header */}
            <div className="lg:hidden mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold text-[#0A0A0A] tracking-tight">
                    {user?.full_name?.split(" ")[0] || "Lender"} Network
                  </h1>
                  <p className="text-xs text-[#888] mt-0.5">{deals.length} deal{deals.length !== 1 ? "s" : ""} in pipeline</p>
                </div>
                {user?.approval_status === "approved" ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="lender-verified-badge">
                    <CheckCircle className="h-3 w-3" /> Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200" data-testid="lender-pending-badge">
                    Pending
                  </span>
                )}
              </div>
              {/* Mobile section toggle */}
              <div className="flex gap-1 mt-3 overflow-x-auto scrollbar-hide" data-testid="lender-section-toggle">
                {NAV_ITEMS.slice(0, 5).map(s => (
                  <button key={s.id} onClick={() => setDashSection(s.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                      dashSection === s.id ? "bg-emerald-700 text-white" : "text-[#888] hover:bg-[#FDFCF9] border border-[#EDE8DC]"
                    }`}>
                    <s.icon className="h-3 w-3" /> {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Entitlement Banner for pending lenders */}
            {user?.status === "pending" && (
              <div className="mb-4">
                <EntitlementBanner
                  type="pending"
                  title="Account Pending Approval"
                  message="Your lender account is under review. You can browse deal flow, but submitting term sheets is locked until approved."
                />
              </div>
            )}

            {/* ── TAB CONTENT ── */}
            {dashSection === "loan_boxes" && <LoanBoxManager />}
            {dashSection === "marketplace" && <MarketplaceDeals onOpenTermSheet={(deal) => openTermSheet(deal)} onNavigateToLoanBoxes={() => setDashSection("loan_boxes")} />}
            {dashSection === "messages" && <EntityComms entityType="lender" entityId={user?.user_id || ""} />}
            {dashSection === "profile" && (() => { navigateTo("profile"); return null; })()}
            {dashSection === "servicing" && <LenderServicingTab userId={user?.user_id} />}
            {dashSection === "pipeline" && <LenderPipelineView onTabChange={setDashSection} />}
            {dashSection === "performance" && <LenderPerformanceCard />}

            {/* Term Sheets Tab */}
            {dashSection === "term_sheets" && (
              <div className="rounded-2xl bg-white border border-[#EDE8DC] p-5 shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                <h3 className="text-sm font-bold text-[#222] mb-4 uppercase tracking-wider">My Submitted Term Sheets</h3>
                {mySheets.length === 0 ? (
                  <div className="text-center py-12 text-[#888]">
                    <FileText className="h-10 w-10 mx-auto text-[#ddd] mb-3" />
                    <p className="text-sm">No term sheets submitted yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {mySheets.map((ts, i) => (
                      <div key={ts.term_sheet_id || i} className="rounded-xl border border-[#EDE8DC] bg-[#FDFCF9] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
                        data-testid={`my-ts-${i}`}>
                        <div>
                          <div className="text-sm font-semibold text-[#222]">{fmtUSD(ts.loan_amount || 0)} at {ts.interest_rate}%</div>
                          <div className="text-[10px] text-[#888] mt-0.5">{[ts.points && `${ts.points} pts`, ts.term_months && `${ts.term_months}mo`, ts.closing_days && `${ts.closing_days}d close`].filter(Boolean).join(' | ') || 'Pending details'}</div>
                        </div>
                        <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-bold ${
                          ts.status === "submitted" ? "bg-blue-100 text-blue-700" :
                          ts.status === "selected" ? "bg-emerald-100 text-emerald-700" :
                          ts.status === "passed" ? "bg-red-100 text-red-600" :
                          ts.status === "closed" ? "bg-green-100 text-green-700" :
                          "bg-gray-100 text-gray-600"
                        }`}>{ts.status === "selected" ? "SELECTED" : ts.status === "passed" ? "NOT SELECTED" : ts.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── DEAL FLOW FEED ── */}
            {dashSection === "deals" && (
              deals.length === 0 ? (
                <div className="rounded-2xl bg-white border border-[#EDE8DC] p-12 text-center shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
                  <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-5">
                    <TrendingUp className="h-7 w-7 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A0A0A] mb-2">No Deals in Pipeline Yet</h3>
                  <p className="text-sm text-[#555]">New opportunities will appear here as borrowers submit applications.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Metrics Panel */}
                  <LenderMetricsPanel />

                  {/* Feed Header + Filters */}
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm font-bold text-[#222] uppercase tracking-wider">Deal Flow</h3>
                      <LivePulse count={deals.length} label="deals live" />
                    </div>
                    <span className="text-[10px] text-[#bbb] font-mono">{filteredDeals.length} showing</span>
                  </div>
                  <FilterPills
                    filters={[
                      { key: "all", label: "All", count: deals.length },
                      { key: "interested", label: "Interested", count: deals.filter(d => d.has_expressed_interest).length },
                      { key: "quoted", label: "Quoted", count: deals.filter(d => hasTermSheet?.(d.lead_id)).length },
                      { key: "selected", label: "Selected" },
                      { key: "active", label: "Active", count: deals.filter(d => ["funding","funded"].includes(d.stage)).length },
                    ]}
                    active={dealFilter}
                    onSelect={setDealFilter}
                    className="mb-2"
                  />

                  {/* Deal Comparison Toolbar */}
                  {filteredDeals.length >= 2 && (
                    <div className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-white border border-[#EDE8DC] text-xs" data-testid="deal-compare-toolbar">
                      <div className="flex items-center gap-2">
                        <span className="text-[#888] font-medium">
                          {selectedDeals.size > 0 ? `${selectedDeals.size} deal${selectedDeals.size > 1 ? "s" : ""} selected` : "Select deals to compare"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedDeals.size > 0 && (
                          <button onClick={() => setSelectedDeals(new Set())}
                            className="text-[10px] text-[#888] hover:text-[#333] font-medium">
                            Clear
                          </button>
                        )}
                        <button
                          onClick={() => setShowDealComparison(true)}
                          disabled={selectedDeals.size < 2}
                          data-testid="open-deal-comparison"
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-emerald-700 text-white hover:bg-emerald-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                          <BarChart3 className="h-3 w-3" /> Compare ({selectedDeals.size})
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Deal Comparison Panel */}
                  <AnimatePresence>
                    {showDealComparison && selectedDeals.size >= 2 && (
                      <DealComparison
                        deals={filteredDeals.filter(d => selectedDeals.has(d.lead_id))}
                        onClose={() => setShowDealComparison(false)}
                        onViewDeal={(leadId) => { setShowDealComparison(false); navigateToEntity("deal", leadId); }}
                      />
                    )}
                  </AnimatePresence>

                  {/* Feed: Activity Box + Deal Cards */}
                  {filteredDeals.length === 0 ? (
                    <div className="rounded-2xl bg-white border border-[#EDE8DC] p-8 text-center">
                      <p className="text-sm text-[#888]">No deals match the "{DEAL_FILTERS.find(f => f.id === dealFilter)?.label}" filter.</p>
                    </div>
                  ) : (
                    <>
                      {/* Activity Feed — scrollable container */}
                      {feedItems.some(item => item.type === "activity") && (
                        <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)] mb-4">
                          <button
                            onClick={() => setFeedCollapsed(!feedCollapsed)}
                            className="w-full flex items-center justify-between px-5 py-3 text-xs font-semibold text-[#555] hover:bg-[#FDFCF9] transition-all"
                            data-testid="lender-feed-collapse-toggle"
                          >
                            <span className="flex items-center gap-2">
                              <Zap className="h-3.5 w-3.5 text-emerald-600" />
                              Activity Feed
                              <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                                {feedItems.filter(item => item.type === "activity").length}
                              </span>
                            </span>
                            {feedCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                          </button>
                          {!feedCollapsed && (
                            <div className="max-h-[280px] overflow-y-auto border-t border-[#EDE8DC] divide-y divide-[#f0ebe0]" data-testid="lender-activity-scroll">
                              {feedItems.filter(item => item.type === "activity").map((item, i) => {
                                const n = item.notif;
                                const nid = n.notif_id || n.notification_id || `lact-${i}`;
                                const entityId = n.entity_id || n.lead_id || n.deal_id;
                                const entityType = n.entity_type || "deal";
                                return (
                                  <div key={nid}
                                    onClick={() => { if (entityId) navigateToEntity(entityType, entityId); }}
                                    className="flex items-start gap-3 px-5 py-3 hover:bg-[#FDFCF9] transition-all cursor-pointer"
                                    data-testid={`lender-feed-activity-${i}`}
                                  >
                                    <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                      n.type === "term_sheet_selected" ? "bg-emerald-50 text-emerald-600" :
                                      n.type === "deal_stage_advanced" ? "bg-blue-50 text-blue-600" :
                                      n.type === "lender_interest" ? "bg-amber-50 text-amber-600" :
                                      "bg-gray-50 text-gray-500"
                                    }`}>
                                      {n.type === "term_sheet_selected" ? <CheckCircle className="h-3.5 w-3.5" /> :
                                       n.type === "deal_stage_advanced" ? <TrendingUp className="h-3.5 w-3.5" /> :
                                       <Zap className="h-3.5 w-3.5" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-medium text-[#333] leading-relaxed">{n.message || n.title || "Platform update"}</p>
                                      <p className="text-[10px] text-[#aaa] mt-1">{_relativeTime(n.created_at)}</p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0 mt-1">
                                      {!n.read && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                                      {entityId && <ChevronRight className="h-3.5 w-3.5 text-[#ccc]" />}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Deal Cards */}
                      {feedItems.filter(item => item.type === "deal").map((item, i) => {
                        const deal = item.deal;
                        const isSelected = selectedDeals.has(deal.lead_id);
                        return (
                          <motion.div key={deal.lead_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.3), ease: "easeOut" }}>
                            <div className="flex items-center gap-2 mb-1.5 px-1">
                              <button
                                onClick={() => { const next = new Set(selectedDeals); if (isSelected) next.delete(deal.lead_id); else if (next.size < 4) next.add(deal.lead_id); setSelectedDeals(next); }}
                                data-testid={`deal-select-${deal.lead_id}`}
                                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all shrink-0 ${
                                  isSelected ? "bg-emerald-600 border-emerald-600 text-white" : "border-[#D4CFC4] hover:border-emerald-400"
                                }`}>
                                {isSelected && <Check className="h-3 w-3" />}
                              </button>
                              {deal.total_interest > 0 && <SocialProofBadge text={`${deal.total_interest} interested`} variant="interest" />}
                              {deal.lender_views > 3 && <SocialProofBadge text={`${deal.lender_views} views`} variant="urgency" />}
                              {deal.stage === "funded" && <SocialProofBadge text="Funded" variant="funded" />}
                            </div>
                            <DealCard
                              deal={deal}
                              user={user}
                              hasTermSheet={hasTermSheet(deal.lead_id)}
                              onCardClick={() => navigateToEntity("deal", deal.lead_id)}
                              onExpressInterest={() => expressInterest(deal.lead_id)}
                              onSubmitTermSheet={() => openTermSheet(deal)}
                              onViewProperty={() => openProperty(deal)}
                              onOpenDealRoom={() => openDealRoom(deal.lead_id)}
                              onView360={() => navigateToEntity("deal", deal.lead_id)}
                              expressing={expressing === deal.lead_id}
                              onDownloadReport={downloadReport}
                              onQuickSubmit={() => { setQuickTsModal(deal.lead_id); setQuickTsForm({ rate: "12.5", ltv: "", term_months: "12", closing_days: "10" }); }}
                              onPassDeal={() => handlePassDeal(deal.lead_id)}
                              onWatchlist={() => handleWatchlist(deal.lead_id)}
                              passingDeal={passingDeal}
                              savingDeal={savingDeal}
                              isViewed={viewedIds.has(deal.lead_id)}
                              onTrackView={trackDealView}
                            />
                          </motion.div>
                        );
                      })}
                    </>
                  )}
                </div>
              )
            )}
          </div>

          {/* ── RIGHT SIDEBAR: Market Intelligence ── */}
          <div className="hidden xl:block">
            <div className="sticky top-6 space-y-4">
              {/* Deal Pipeline Summary */}
              <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                <div className="px-4 py-3 border-b border-[#EDE8DC]">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#888]">Pipeline Status</h3>
                </div>
                <div className="p-3 space-y-1.5">
                  {deals.slice(0, 8).map((deal) => {
                    const rc = riskColor(deal.summary?.risk_score);
                    return (
                      <button key={deal.lead_id}
                        onClick={() => { setDashSection("deals"); navigateToEntity("deal", deal.lead_id); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-[#FDFCF9] transition-colors text-left">
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-bold text-[#0A0A0A] truncate">{deal.deal_number || deal.property_address?.substring(0, 20)}</div>
                          <div className="text-[9px] text-[#999]">{loanTypeLabels[deal.loan_type] || deal.loan_type}</div>
                        </div>
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${stageBadgeColor(deal.stage)}`}>{stageLabel(deal.stage)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* My Term Sheets Status */}
              {mySheets.length > 0 && (
                <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                  <div className="px-4 py-3 border-b border-[#EDE8DC]">
                    <h3 className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#888]">Term Sheet Status</h3>
                  </div>
                  <div className="p-3 space-y-2">
                    {mySheets.slice(0, 5).map((ts, i) => (
                      <div key={ts.term_sheet_id || i} className="flex items-center justify-between py-2 border-b border-[#EDE8DC] last:border-0">
                        <span className="text-[10px] font-medium text-[#333] truncate max-w-[120px]">{fmtUSD(ts.loan_amount || 0)}</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold ${
                          ts.status === "selected" ? "bg-emerald-100 text-emerald-700" :
                          ts.status === "submitted" ? "bg-blue-100 text-blue-700" :
                          ts.status === "passed" ? "bg-red-100 text-red-600" :
                          "bg-gray-100 text-gray-600"
                        }`}>{ts.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                <div className="px-4 py-3 border-b border-[#EDE8DC]">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#888]">Quick Actions</h3>
                </div>
                <div className="p-2 space-y-1">
                  {[
                    { icon: ShoppingBag, label: "Browse Marketplace", action: () => setDashSection("marketplace") },
                    { icon: Box, label: "Manage Loan Boxes", action: () => setDashSection("loan_boxes") },
                    { icon: FileText, label: "View Term Sheets", action: () => setDashSection("term_sheets") },
                    { icon: MessageCircle, label: "Messages", action: () => setDashSection("messages") },
                  ].map((a, i) => (
                    <button key={i} onClick={a.action}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs text-[#555] font-medium hover:bg-[#FDFCF9] hover:text-[#333] transition-colors">
                      <a.icon className="h-3.5 w-3.5 text-[#bbb]" /> {a.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Term Sheet Modal */}
      <AnimatePresence>
        {tsModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-cream backdrop-blur-sm p-4" onClick={() => setTsModal(null)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()} data-testid="term-sheet-modal"
              className="relative w-full max-w-lg rounded-[2rem] border border-cream-warm bg-gradient-to-br from-[#03352a]/98 via-[#022c22]/98 to-[#044332]/98 p-5 sm:p-8 shadow-2xl max-h-[85vh] overflow-y-auto">
              <button onClick={() => setTsModal(null)} className="absolute top-4 right-4 p-2 text-[#999] hover:text-[#0A0A0A]"><X className="h-5 w-5" /></button>
              <h3 className="text-xl font-bold text-[#0A0A0A] mb-6 flex items-center gap-2"><FileText className="h-5 w-5 text-gold-dark" /> Submit Term Sheet</h3>

              <form onSubmit={submitTermSheet} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <label className="block"><span className={labelClass}>Rate (%)</span>
                    <input required type="number" step="0.1" min="0" max="100" value={tsForm.interest_rate}
                      onChange={e => setTsForm(f => ({ ...f, interest_rate: e.target.value }))} className={inputClass} data-testid="ts-rate" />
                    <p className="mt-1 text-[10px] text-[#888]">Current Texas private lending market: 12%-16% + 1.5-3 points.</p>
                  </label>
                  <label className="block"><span className={labelClass}>Points</span>
                    <input required type="number" step="0.25" min="0" max="20" value={tsForm.points}
                      onChange={e => setTsForm(f => ({ ...f, points: e.target.value }))} className={inputClass} data-testid="ts-points" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <label className="block"><span className={labelClass}>Loan Amount ($)</span>
                    <input required type="text" value={tsForm.loan_amount ? Number(String(tsForm.loan_amount).replace(/[^0-9.]/g, '')).toLocaleString('en-US') : ''}
                      onChange={e => setTsForm(f => ({ ...f, loan_amount: e.target.value.replace(/[^0-9.]/g, '') }))} className={inputClass} placeholder="300,000" data-testid="ts-amount" />
                  </label>
                  <label className="block"><span className={labelClass}>Term (months)</span>
                    <input required type="number" min="1" max="360" value={tsForm.term_months}
                      onChange={e => setTsForm(f => ({ ...f, term_months: e.target.value }))} className={inputClass} data-testid="ts-term" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <label className="block"><span className={labelClass}>Closing (days)</span>
                    <input required type="number" min="1" max="120" value={tsForm.closing_days}
                      onChange={e => setTsForm(f => ({ ...f, closing_days: e.target.value }))} className={inputClass} data-testid="ts-close" />
                  </label>
                  <label className="block"><span className={labelClass}>Payment Structure</span>
                    <select value={tsForm.payment_structure} onChange={e => setTsForm(f => ({ ...f, payment_structure: e.target.value }))}
                      className={inputClass} data-testid="ts-payment-structure">
                      <option value="interest_only">Interest Only</option>
                      <option value="principal_interest">Principal + Interest (Amortizing)</option>
                    </select>
                  </label>
                </div>
                {tsForm.loan_amount && tsForm.interest_rate && tsForm.term_months && (() => {
                  const amt = parseFloat(String(tsForm.loan_amount).replace(/[^0-9.]/g, '')) || 0;
                  const r = (parseFloat(tsForm.interest_rate) || 0) / 100 / 12;
                  const n = parseInt(tsForm.term_months) || 12;
                  let mo;
                  if (tsForm.payment_structure === "principal_interest" && r > 0) {
                    mo = amt * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
                  } else {
                    mo = amt * r;
                  }
                  const label = tsForm.payment_structure === "principal_interest" ? "fully amortizing" : "+ balloon at maturity";
                  return mo > 0 ? (
                    <div className="rounded-lg bg-emerald-900/30 border border-emerald-700/30 px-3 py-2 text-xs text-cream/80" data-testid="ts-monthly-calc">
                      Est. Monthly Payment: <span className="font-bold text-gold">${Math.round(mo).toLocaleString()}/mo</span> <span className="text-cream/50">{label}</span>
                    </div>
                  ) : null;
                })()}
                <PrepaymentPenaltyInput
                  value={tsForm.prepayment_penalty_structure}
                  onChange={v => setTsForm(f => ({ ...f, prepayment_penalty_structure: v }))}
                  loanAmount={parseFloat(tsForm.loan_amount) || 0}
                  rate={parseFloat(tsForm.interest_rate) || 0}
                  data-testid="ts-prepay"
                />
                <label className="block"><span className={labelClass}>Conditions</span>
                  <textarea value={tsForm.conditions} onChange={e => setTsForm(f => ({ ...f, conditions: e.target.value }))}
                    className={inputClass + " resize-none"} rows={2} placeholder="Any conditions or requirements..." data-testid="ts-conditions" />
                </label>
                <label className="block"><span className={labelClass}>Notes</span>
                  <textarea value={tsForm.notes} onChange={e => setTsForm(f => ({ ...f, notes: e.target.value }))}
                    className={inputClass + " resize-none"} rows={2} placeholder="Additional notes..." data-testid="ts-notes" />
                </label>

                {tsError && <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-300">{tsError}</div>}

                <div className="flex gap-3 pt-2">
                  <GoldButton type="submit" disabled={tsSubmitting} data-testid="ts-submit-btn">
                    {tsSubmitting ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Submitting...</span> : "Submit Term Sheet"}
                  </GoldButton>
                  <GoldButton type="button" variant="secondary" onClick={() => setTsModal(null)}>Cancel</GoldButton>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Deal Room Modal */}
      <AnimatePresence>
        {dealRoom && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#faf7f2]/80 backdrop-blur-sm p-4" onClick={() => setDealRoom(null)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()} data-testid="deal-room-modal"
              className="relative w-full max-w-lg rounded-2xl border border-[#e8e0d0] bg-white p-6 shadow-2xl flex flex-col" style={{ maxHeight: "80vh" }}>
              <button onClick={() => setDealRoom(null)} className="absolute top-4 right-4 p-2 text-[#999] hover:text-[#0A0A0A] z-10"><X className="h-5 w-5" /></button>
              <div className="flex items-center gap-2 mb-4">
                <MessageCircle className="h-5 w-5 text-emerald-700" />
                <span className="text-lg font-bold text-[#0A0A0A]">Deal Room</span>
              </div>
              <DealRoomChat leadId={dealRoom} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Property Detail Modal */}
      <AnimatePresence>
        {propertyModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-cream backdrop-blur-sm p-4" onClick={() => setPropertyModal(null)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()} data-testid="property-detail-modal"
              className="relative w-full max-w-3xl rounded-[2rem] border border-cream-warm bg-gradient-to-br from-[#03352a]/98 via-[#022c22]/98 to-[#044332]/98 p-6 shadow-2xl max-h-[88vh] overflow-y-auto">
              <button onClick={() => setPropertyModal(null)} className="absolute top-4 right-4 p-2 text-[#999] hover:text-[#0A0A0A] z-10"><X className="h-5 w-5" /></button>

              {propertyLoading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
              ) : propertyData ? (
                <PropertyDetailView deal={propertyModal} data={propertyData} onSubmitTerm={() => { setPropertyModal(null); setTsModal(propertyModal); }} />
              ) : (
                <div className="text-center py-16 text-[#999]">Failed to load property data</div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Term Sheet Modal */}
      <AnimatePresence>
        {quickTsModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setQuickTsModal(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-white rounded-2xl border border-[#EDE8DC] shadow-xl max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="quick-ts-modal">
              <div className="px-5 py-4 border-b border-[#EDE8DC] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-emerald-600" />
                  <h3 className="text-sm font-bold text-[#0A0A0A]">Quick Term Sheet</h3>
                </div>
                <button onClick={() => setQuickTsModal(null)} className="p-1 rounded-full hover:bg-[#f5f0e8]"><X className="h-4 w-4 text-[#888]" /></button>
              </div>
              <form onSubmit={submitQuickTs} className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-[#888] uppercase mb-1">Rate (%)</label>
                    <input type="number" step="0.1" min="0" required value={quickTsForm.rate} data-testid="quick-ts-rate"
                      onChange={e => setQuickTsForm(f => ({...f, rate: e.target.value}))}
                      className="w-full px-3 py-2 rounded-lg border border-[#EDE8DC] text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-[#888] uppercase mb-1">Max LTV</label>
                    <input type="number" step="0.01" min="0" max="1" value={quickTsForm.ltv} data-testid="quick-ts-ltv"
                      onChange={e => setQuickTsForm(f => ({...f, ltv: e.target.value}))}
                      placeholder="0.75"
                      className="w-full px-3 py-2 rounded-lg border border-[#EDE8DC] text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-[#888] uppercase mb-1">Term (mo)</label>
                    <input type="number" min="1" required value={quickTsForm.term_months} data-testid="quick-ts-term"
                      onChange={e => setQuickTsForm(f => ({...f, term_months: e.target.value}))}
                      className="w-full px-3 py-2 rounded-lg border border-[#EDE8DC] text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-[#888] uppercase mb-1">Closing (days)</label>
                    <input type="number" min="1" required value={quickTsForm.closing_days} data-testid="quick-ts-closing"
                      onChange={e => setQuickTsForm(f => ({...f, closing_days: e.target.value}))}
                      className="w-full px-3 py-2 rounded-lg border border-[#EDE8DC] text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                  </div>
                </div>
                <p className="text-[10px] text-[#999]">All other fields auto-populated from your lender profile.</p>
                <GoldButton type="submit" className="w-full !py-2.5 text-xs" disabled={quickTsSubmitting} data-testid="quick-ts-submit">
                  {quickTsSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" /> : <Zap className="h-3.5 w-3.5 inline mr-1.5" />}
                  Submit Term Sheet
                </GoldButton>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Deal Detail Modal */}
      {detailLeadId && <DealDetailModal leadId={detailLeadId} onClose={() => setDetailLeadId(null)} viewerRole="lender" />}
    </div>
  );
}

function _relativeTime(ts) {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

/* ── Map fallback when no property photo ── */
function getMapUrl(address) {
  if (!address) return null;
  const encoded = encodeURIComponent(address);
  // Google Maps Static API — requires REACT_APP_GOOGLE_MAPS_KEY env var
  const key = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  if (key) {
    return `https://maps.googleapis.com/maps/api/staticmap?center=${encoded}&zoom=16&size=600x300&maptype=roadmap&markers=color:red%7C${encoded}&key=${key}`;
  }
  return null;
}

/* ── Fallback property photos by loan type (used when no photo AND no maps key) ── */
const FALLBACK_PHOTOS = {
  fix_flip: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=600&q=80",
  bridge: "https://images.unsplash.com/photo-1582407947092-a75e4d4aee11?auto=format&fit=crop&w=600&q=80",
  dscr: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=600&q=80",
  construction: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=600&q=80",
  ground_up: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=600&q=80",
  multifamily: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=600&q=80",
  commercial: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=600&q=80",
  default: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=600&q=80",
};

function riskColor(score) {
  if (!score) return { text: "text-[#999]", bg: "bg-gray-500/20 text-gray-300 border-gray-400/30" };
  if (score >= 75) return { text: "text-emerald-600", bg: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30" };
  if (score >= 60) return { text: "text-amber-600", bg: "bg-amber-500/20 text-amber-300 border-amber-400/30" };
  return { text: "text-red-500", bg: "bg-red-500/20 text-red-300 border-red-400/30" };
}

function DealCard({ deal, user, hasTermSheet: hasTerm, onCardClick, onExpressInterest, onSubmitTermSheet, onViewProperty, onOpenDealRoom, onView360, expressing, onDownloadReport, onQuickSubmit, onPassDeal, onWatchlist, passingDeal, savingDeal, isViewed, onTrackView }) {
  const riskScore = deal.summary?.risk_score;
  const loanAmt = deal.requested_loan_amount || deal.purchase_price || 0;
  const isWon = deal.selected_lender_name && deal.selected_lender_name === user?.full_name;
  const ltvPct = deal.ltv ? `${(deal.ltv * 100).toFixed(1)}%` : "N/A";
  const arvDisplay = deal.arv ? fmtUSDCompact(deal.arv) : "N/A";
  const loanDisplay = fmtUSDCompact(loanAmt);

  // Derive address parts
  const formatted = formatAddress(deal.property_address || "");
  const parts = formatted.split(",").map(s => s.trim());
  const street = parts[0] || "Address Pending";
  const cityState = parts.length > 1 ? parts.slice(1).join(", ") : (deal.state || "");

  // AI brief one-liner
  const aiBrief = deal.lender_brief?.headline || deal.lender_brief?.opportunity_summary || "";

  // Stage label for single status badge
  const displayStatus = isWon ? "Won" : hasTerm ? "Quoted" : stageLabel(deal.stage);

  return (
    <div className="ic-deal-card" data-testid={`lender-deal-card-${deal.lead_id}`}>
      {/* Map Thumbnail */}
      <div className="ic-deal-card-map" onClick={() => { onTrackView?.(deal.lead_id); onCardClick?.(); }}>
        <MapThumbnail address={deal.property_address} height={140} />
        <span className="ic-deal-card-map-overlay-lt">
          {loanTypeLabels[deal.loan_type] || deal.loan_type}
        </span>
        {riskScore != null && (
          <div className="ic-deal-card-map-overlay-score">
            <ScoreBadge score={riskScore} />
          </div>
        )}
      </div>

      {/* Card Body */}
      <div className="ic-deal-card-body">
        {/* Row 1: Address */}
        <div className="ic-deal-card-address">{street}</div>
        <div className="ic-deal-card-city">{cityState}</div>
        <div className="ic-deal-card-id">{deal.deal_number || `Deal ${deal.lead_id?.substring(0, 8)}`}</div>

        {/* Row 2: MetricPills */}
        <div className="ic-deal-card-pills">
          <MetricPill label="Loan" value={loanDisplay} highlight />
          <MetricPill label="LTV" value={ltvPct} />
          <MetricPill label="ARV" value={arvDisplay} />
        </div>

        {/* Row 3: Context line */}
        <div className="ic-deal-card-context">
          {[
            deal.experience > 0 ? `${deal.experience} projects` : null,
            loanTypeLabels[deal.loan_type] || deal.loan_type,
            deal.close_timeline_days ? `${deal.close_timeline_days} days to close` : null,
          ].filter(Boolean).join(" \u00B7 ")}
        </div>

        {/* Row 4: AI Brief (one line) */}
        {aiBrief && (
          <div className="ic-deal-card-ai-brief" data-testid={`lender-brief-${deal.lead_id}`} title={aiBrief}>
            {aiBrief}
          </div>
        )}

        {/* Row 5: Status badge */}
        <div className="ic-deal-card-status">
          <StatusBadge status={displayStatus} />
        </div>

        {/* Pending approval notice */}
        {deal.has_expressed_interest && !deal.access_accepted && deal.lender_status !== "rejected" && (
          <div style={{ padding: "8px", borderRadius: "var(--ic-radius)", border: "1px solid rgba(217,119,6,0.2)", background: "rgba(217,119,6,0.05)", color: "var(--ic-status-pending)", fontSize: 11, fontWeight: 500, textAlign: "center", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }} data-testid={`pending-notice-${deal.lead_id}`}>
            <Lock className="h-3 w-3" /> Awaiting approval for full access
          </div>
        )}

        {/* Borrower Contact Info — Revealed after admin approval */}
        {deal.lender_approved_for_deal && deal.borrower_name && (
          <div style={{ padding: 10, borderRadius: "var(--ic-radius)", border: "1px solid rgba(64,145,108,0.2)", background: "rgba(64,145,108,0.05)", marginBottom: 12 }} data-testid={`borrower-revealed-${deal.lead_id}`}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ic-green-dark)", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
              <Unlock className="h-3 w-3" /> Borrower Details Revealed
            </div>
            <div style={{ fontSize: 12, color: "var(--ic-text-primary)" }}>
              <p><span style={{ fontWeight: 500 }}>Name:</span> {deal.borrower_name}</p>
              {deal.borrower_entity && <p><span style={{ fontWeight: 500 }}>Entity:</span> {deal.borrower_entity}</p>}
              {deal.borrower_email && <p><span style={{ fontWeight: 500 }}>Email:</span> {deal.borrower_email}</p>}
            </div>
          </div>
        )}

        {/* Row 6: Action Buttons */}
        <div className="ic-deal-card-buttons">
          {hasTerm ? (
            deal.pending_lender_approval ? (
              <button className="ic-btn-outline" style={{ flex: 1 }} disabled data-testid={`awaiting-imaani-${deal.lead_id}`}>
                <Clock className="h-3.5 w-3.5 inline mr-1.5" />Awaiting Approval
              </button>
            ) : (
              <button className="ic-btn-outline" style={{ flex: 1, cursor: "default", borderColor: "var(--ic-gold)", color: "var(--ic-gold)" }} data-testid={`ts-submitted-${deal.lead_id}`}>
                <FileText className="h-3.5 w-3.5 inline mr-1.5" />Term Sheet Submitted
              </button>
            )
          ) : deal.access_accepted ? (
            <>
              <button onClick={onSubmitTermSheet} className="ic-btn-primary" data-testid={`submit-ts-${deal.lead_id}`}>
                Submit Term Sheet
              </button>
              <button onClick={onQuickSubmit} className="ic-btn-outline" data-testid={`quick-ts-${deal.lead_id}`}>
                Quick Quote
              </button>
            </>
          ) : !deal.has_expressed_interest ? (
            <>
              <button onClick={onExpressInterest} className="ic-btn-primary" disabled={expressing} data-testid={`express-interest-${deal.lead_id}`}>
                {expressing ? <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" /> : null}
                Express Interest
              </button>
              <button onClick={onQuickSubmit} className="ic-btn-outline" data-testid={`quick-ts-${deal.lead_id}`}>
                Quick Quote
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function PropertyDetailView({ deal, data, onSubmitTerm }) {
  const { property, valuation, neighborhood, map } = data;
  const ltLabel = loanTypeLabels[deal.loan_type] || deal.loan_type;

  return (
    <div data-testid="property-detail-content">
      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-400/20 to-gold/10 border border-cream-warm flex items-center justify-center shrink-0">
          <Home className="h-6 w-6 text-gold-dark" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-[#0A0A0A]">{property.address}</h3>
          <div className="flex gap-2 mt-1 flex-wrap">
            <span className="px-2.5 py-0.5 rounded-lg text-[11px] bg-gold/15 text-gold border border-cream-warm">{ltLabel}</span>
            <span className="text-sm text-[#888] flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{property.state}</span>
            <span className="text-sm text-[#888]">{property.property_type}</span>
            {data.source === "simulated" && (
              <span className="px-2 py-0.5 rounded-md text-[10px] bg-amber-400/10 text-amber-600/60 border border-amber-400/15">Estimated Data</span>
            )}
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="rounded-xl overflow-hidden border border-cream-warm mb-6" data-testid="property-map">
        <iframe
          title="Property Location"
          src={map.embed_url}
          width="100%"
          height="250"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="w-full"
        />
      </div>

      {/* Property Specs */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
        {[
          { label: "Beds", value: property.bedrooms, icon: Home },
          { label: "Baths", value: property.bathrooms, icon: Layers },
          { label: "Sqft", value: property.sqft?.toLocaleString(), icon: Ruler },
          { label: "Lot", value: `${(property.lot_sqft / 1000).toFixed(1)}k`, icon: Ruler },
          { label: "Built", value: property.year_built, icon: Calendar },
          { label: "Stories", value: property.stories, icon: Layers },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-cream-warm bg-cream p-3 text-center">
            <s.icon className="h-3.5 w-3.5 text-gold/50 mx-auto mb-1" />
            <div className="text-base font-semibold text-[#0A0A0A]">{s.value}</div>
            <div className="text-[10px] text-[#999]">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Valuation */}
      <div className="rounded-xl border border-cream-warm bg-[#022c22]/40 p-5 mb-6">
        <h4 className="text-sm font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-gold-dark" /> Valuation & Financials
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "AVM Estimate", value: fmtUSD(valuation.avm_estimate), color: "text-gold-dark" },
            { label: "Tax Assessed", value: fmtUSD(valuation.tax_assessed), color: "text-[#0A0A0A]" },
            { label: "$/Sqft", value: `$${valuation.price_per_sqft}`, color: "text-[#0A0A0A]" },
            { label: `Last Sale (${valuation.last_sale_year})`, value: fmtUSD(valuation.last_sale_price), color: "text-[#444]" },
          ].map((v) => (
            <div key={v.label}>
              <div className={`text-lg font-semibold ${v.color}`}>{v.value}</div>
              <div className="text-[10px] text-[#999] mt-0.5">{v.label}</div>
            </div>
          ))}
        </div>

        {(valuation.arv || valuation.rehab_budget) && (
          <div className="mt-4 pt-4 border-t border-cream-warm grid grid-cols-2 md:grid-cols-4 gap-4">
            {valuation.arv && (
              <div>
                <div className="text-lg font-semibold text-emerald-600">{fmtUSD(valuation.arv)}</div>
                <div className="text-[10px] text-[#999]">After-Repair Value</div>
              </div>
            )}
            {valuation.rehab_budget > 0 && (
              <div>
                <div className="text-lg font-semibold text-amber-600">${valuation.rehab_budget.toLocaleString()}</div>
                <div className="text-[10px] text-[#999]">Rehab Budget</div>
              </div>
            )}
            <div>
              <div className="text-lg font-semibold text-[#0A0A0A]">${deal.purchase_price?.toLocaleString()}</div>
              <div className="text-[10px] text-[#999]">Purchase Price</div>
            </div>
            {deal.requested_loan_amount && (
              <div>
                <div className="text-lg font-semibold text-blue-600">${deal.requested_loan_amount.toLocaleString()}</div>
                <div className="text-[10px] text-[#999]">Requested Loan</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Neighborhood */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="rounded-xl border border-cream-warm bg-cream p-4">
          <div className="text-xs text-[#999] mb-1">Median Home Value</div>
          <div className="text-lg font-semibold text-[#0A0A0A]">${neighborhood.median_home_value?.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-cream-warm bg-cream p-4">
          <div className="text-xs text-[#999] mb-1">Avg Days on Market</div>
          <div className="text-lg font-semibold text-[#0A0A0A]">{neighborhood.avg_days_on_market} days</div>
        </div>
      </div>

      {/* Deal Summary */}
      <div className="rounded-xl border border-cream-warm bg-cream p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><span className="text-[#999]">FICO:</span> <span className="text-[#0A0A0A] font-medium ml-1">{deal.fico_estimate || "—"}</span></div>
          <div><span className="text-[#999]">Experience:</span> <span className="text-[#0A0A0A] font-medium ml-1">{deal.experience || 0} projects</span></div>
          <div><span className="text-[#999]">Stage:</span> <span className={`font-medium ml-1 text-xs px-2 py-0.5 rounded-full ${stageBadgeColor(deal.stage)}`}>{stageLabel(deal.stage)}</span></div>
          <div><span className="text-[#999]">Interest:</span> <span className="text-[#0A0A0A] font-medium ml-1">{deal.total_interest} lenders</span></div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <GoldButton onClick={onSubmitTerm} className="flex-1" data-testid="property-submit-term">
          <FileText className="h-4 w-4 mr-2 inline" /> Submit Term Sheet
        </GoldButton>
        <a href={`https://www.google.com/maps/search/${encodeURIComponent(property.address)}`} target="_blank" rel="noopener noreferrer"
          className="px-4 py-3 rounded-xl border border-cream-warm text-[#555] hover:text-[#0A0A0A] hover:border-gold/40 transition-colors flex items-center gap-2 text-sm" data-testid="property-open-maps">
          <ExternalLink className="h-4 w-4" /> Open in Maps
        </a>
      </div>
    </div>
  );
}


function LenderServicingTab({ userId }) {
  const [servicing, setServicing] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/servicing/lender/${userId}`)
      .then(r => setServicing(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId]);
  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  return <LenderServicingPanel servicing={servicing} />;
}


/* ═══════════════════════════════════════════════════════════════
   Lender Pipeline View — 5 grouped buckets with deal detail panel
   ═══════════════════════════════════════════════════════════════ */

const BUCKET_CONFIG = {
  needs_response: { label: "Needs Your Response", color: "text-amber-700", bg: "bg-amber-50 border-amber-200", icon: Clock },
  in_discussion: { label: "In Discussion", color: "text-blue-700", bg: "bg-blue-50 border-blue-200", icon: MessageCircle },
  term_sheet_out: { label: "Term Sheet Out", color: "text-purple-700", bg: "bg-purple-50 border-purple-200", icon: FileText },
  closing: { label: "Closing", color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", icon: CheckCircle },
  funded: { label: "Funded", color: "text-[#B8943E]", bg: "bg-[#E5C98B]/10 border-[#E5C98B]/30", icon: DollarSign },
};

function LenderPipelineView() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [selectedDeal, setSelectedDeal] = React.useState(null);
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [chatLeadId, setChatLeadId] = React.useState(null);

  React.useEffect(() => {
    axios.get(`${API}/lender/pipeline`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const openDealPanel = (deal) => {
    setSelectedDeal(deal);
    setPanelOpen(true);
  };

  const closeDealPanel = () => {
    setPanelOpen(false);
    setTimeout(() => setSelectedDeal(null), 300);
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!data) return <p className="text-sm text-[#888] text-center py-8">Unable to load pipeline.</p>;

  const buckets = data.buckets || {};
  const fmtAmt = (n) => n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${n}`;

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      const days = Math.floor((Date.now() - d.getTime()) / 86400000);
      if (days === 0) return "today";
      if (days === 1) return "1d ago";
      return `${days}d ago`;
    } catch { return ""; }
  };

  return (
    <div className="space-y-4" data-testid="lender-pipeline-view">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-[#0A0A0A]">Your Pipeline</h2>
        <span className="text-[10px] text-[#888]">{data.total} total deals</span>
      </div>

      {/* Bucket summary badges — 2-column grid on mobile, row on desktop */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2" data-testid="pipeline-bucket-pills">
        {Object.entries(BUCKET_CONFIG).map(([key, cfg]) => {
          const count = buckets[key]?.count || 0;
          return (
            <div key={key} className={`px-3 py-2 sm:py-1.5 rounded-lg border text-xs font-bold flex items-center gap-1.5 ${cfg.bg}`} data-testid={`bucket-badge-${key}`}>
              <cfg.icon className={`h-3.5 w-3.5 ${cfg.color}`} />
              <span className={`${cfg.color} truncate text-[11px] sm:text-xs`}>{cfg.label}</span>
              <span className={`ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-bold ${count > 0 ? "bg-white " + cfg.color : "bg-white/50 text-[#bbb]"}`}>{count}</span>
            </div>
          );
        })}
      </div>

      {/* Bucket sections */}
      {Object.entries(BUCKET_CONFIG).map(([key, cfg]) => {
        const bucket = buckets[key] || { count: 0, deals: [] };
        if (bucket.count === 0) return null;
        const BucketIcon = cfg.icon;
        return (
          <div key={key} className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid={`bucket-section-${key}`}>
            <div className="px-4 py-3 bg-[#faf7f2] border-b border-[#e8e0d0] flex items-center gap-2">
              <BucketIcon className={`h-4 w-4 ${cfg.color}`} />
              <span className="text-xs font-bold text-[#0A0A0A]">{cfg.label}</span>
              <span className={`ml-auto px-2 py-0.5 rounded-full text-[9px] font-bold border ${cfg.bg}`}>{bucket.count}</span>
            </div>
            <div className="divide-y divide-[#f0ebe0]">
              {bucket.deals.map((deal, i) => (
                <div key={deal.lead_id || i}
                  onClick={() => openDealPanel(deal)}
                  className="px-4 py-3 cursor-pointer hover:bg-[#faf7f2] transition-colors group"
                  data-testid={`pipeline-deal-${deal.deal_number}`}>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-[#0A0A0A]">{deal.deal_number}</span>
                        <span className="px-1.5 py-0.5 rounded-md text-[8px] font-bold bg-[#f5f0e8] text-[#888]">{(deal.loan_type || "").replace(/_/g, " ").toUpperCase()}</span>
                        {deal.state && <span className="text-[10px] text-[#999]">{deal.state}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-[#888]">
                        {deal.ltv && <span>LTV: {(deal.ltv * 100).toFixed(0)}%</span>}
                        {deal.loan_amount > 0 && <span>{fmtAmt(deal.loan_amount)}</span>}
                        {deal.match_score > 0 && <span className="text-emerald-600 font-bold">Fit: {deal.match_score}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] text-[#aaa]">{deal.days_since_matched}d ago</span>
                      <ChevronRight className="h-4 w-4 text-[#ccc] group-hover:text-emerald-600 transition-colors" />
                    </div>
                  </div>

                  {/* Inline term sheet summary for Term Sheet Out bucket */}
                  {key === "term_sheet_out" && deal.term_sheet && (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-purple-50/50 border border-purple-100 text-[11px]" data-testid={`inline-ts-${deal.deal_number}`}>
                      <div className="flex items-center gap-1.5 text-purple-700 font-semibold">
                        <FileText className="h-3 w-3" />
                        <span>Your terms: {fmtAmt(deal.term_sheet.loan_amount || 0)} at {deal.term_sheet.interest_rate}%{deal.term_sheet.points ? ` — ${deal.term_sheet.points}pts` : ""} — submitted {formatTimeAgo(deal.term_sheet.submitted_at)} — Status: <span className="uppercase">{deal.term_sheet.status === "submitted" ? "Under Review" : deal.term_sheet.status}</span></span>
                      </div>
                    </div>
                  )}

                  {/* Inline funded info for Funded bucket */}
                  {key === "funded" && (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-[#E5C98B]/10 border border-[#E5C98B]/20 text-[11px]" data-testid={`inline-funded-${deal.deal_number}`}>
                      <div className="flex items-center gap-1.5 text-[#B8943E] font-semibold">
                        <DollarSign className="h-3 w-3" />
                        <span>Funded {deal.stage === "funded" && deal.days_since_matched ? `${deal.days_since_matched}d ago` : ""} {deal.term_sheet ? `• Your terms: ${fmtAmt(deal.term_sheet.loan_amount || 0)} at ${deal.term_sheet.interest_rate}%` : "• Deal completed"}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* ── Deal Detail Slide-in Panel ── */}
      <AnimatePresence>
        {panelOpen && selectedDeal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/30 z-40"
              onClick={closeDealPanel}
            />
            {/* Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 w-full sm:w-[480px] bg-white z-50 shadow-2xl overflow-y-auto"
              data-testid="pipeline-deal-panel"
            >
              <PipelineDealPanel deal={selectedDeal} onClose={closeDealPanel} onOpenChat={(id) => setChatLeadId(id)} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Deal Room Chat Modal */}
      {chatLeadId && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#EDE8DC]">
              <span className="text-sm font-bold text-[#0A0A0A]">Deal Messages</span>
              <button onClick={() => setChatLeadId(null)} className="p-1 rounded-lg hover:bg-gray-100"><X className="h-4 w-4" /></button>
            </div>
            <div className="h-[60vh]">
              <DealRoomChat leadId={chatLeadId} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Pipeline Deal Detail Panel Content ── */
function PipelineDealPanel({ deal, onClose, onOpenChat }) {
  const fmtAmt = (n) => n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${n}`;
  const ts = deal.term_sheet;
  const timeline = deal.timeline || [];
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [editForm, setEditForm] = React.useState({});
  const [editErrors, setEditErrors] = React.useState({});
  const [currentTs, setCurrentTs] = React.useState(ts);

  const canEdit = currentTs && currentTs.status !== "selected" && currentTs.status !== "rejected" && currentTs.status !== "passed";

  const startEditing = () => {
    setEditForm({
      interest_rate: currentTs?.interest_rate || "",
      points: currentTs?.points || "",
      loan_amount: currentTs?.loan_amount || "",
      term_months: currentTs?.term_months || "",
      closing_days: currentTs?.closing_days || "",
      payment_structure: currentTs?.payment_structure || "interest_only",
      prepayment_penalty_structure: currentTs?.prepayment_penalty_structure || { type: "none" },
      conditions: currentTs?.conditions || "",
      notes: currentTs?.notes || "",
    });
    setEditErrors({});
    setEditing(true);
  };

  const validateForm = () => {
    const errs = {};
    if (editForm.interest_rate !== "" && Number(editForm.interest_rate) < 12.0) errs.interest_rate = "Min 12.0%";
    if (editForm.points !== "" && Number(editForm.points) < 1.0) errs.points = "Min 1.0";
    setEditErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const saveEdit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = {};
      if (editForm.interest_rate !== "") payload.interest_rate = Number(editForm.interest_rate);
      if (editForm.points !== "") payload.points = Number(editForm.points);
      if (editForm.loan_amount !== "") payload.loan_amount = Number(editForm.loan_amount);
      if (editForm.term_months !== "") payload.term_months = Number(editForm.term_months);
      if (editForm.closing_days !== "") payload.closing_days = Number(editForm.closing_days);
      if (editForm.payment_structure) payload.payment_structure = editForm.payment_structure;
      if (editForm.prepayment_penalty_structure) payload.prepayment_penalty_structure = editForm.prepayment_penalty_structure;
      if (editForm.conditions !== undefined) payload.conditions = editForm.conditions;
      if (editForm.notes !== undefined) payload.notes = editForm.notes;

      const res = await axios.put(`${API}/lender/term-sheet/${currentTs.term_sheet_id}`, payload);
      if (res.data.status === "success") {
        toast.success("Your terms have been updated");
        // Update local state with new values
        setCurrentTs(prev => ({
          ...prev,
          interest_rate: payload.interest_rate ?? prev.interest_rate,
          points: payload.points ?? prev.points,
          loan_amount: payload.loan_amount ?? prev.loan_amount,
          term_months: payload.term_months ?? prev.term_months,
          closing_days: payload.closing_days ?? prev.closing_days,
          prepayment_penalty: payload.prepayment_penalty ?? prev.prepayment_penalty,
          conditions: payload.conditions ?? prev.conditions,
          notes: payload.notes ?? prev.notes,
        }));
        setEditing(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update term sheet");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return "—"; }
  };

  const tsStatusColor = (status) => {
    if (status === "selected") return "bg-emerald-100 text-emerald-700";
    if (status === "submitted") return "bg-blue-100 text-blue-700";
    if (status === "rejected" || status === "passed") return "bg-red-100 text-red-600";
    return "bg-gray-100 text-gray-600";
  };

  const EditField = ({ label, field, type = "text", prefix, suffix }) => (
    <div>
      <label className="text-[10px] text-[#888] uppercase tracking-wider font-semibold">{label}</label>
      <div className="relative mt-0.5">
        {prefix && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-[#aaa]">{prefix}</span>}
        <input
          type={type}
          value={editForm[field] || ""}
          onChange={e => setEditForm(prev => ({ ...prev, [field]: e.target.value }))}
          className={`w-full text-xs border rounded-lg px-2 py-1.5 ${prefix ? "pl-5" : ""} ${suffix ? "pr-6" : ""} ${editErrors[field] ? "border-red-300 bg-red-50" : "border-[#EDE8DC]"} focus:outline-none focus:ring-1 focus:ring-emerald-300`}
          data-testid={`edit-ts-${field}`}
        />
        {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#aaa]">{suffix}</span>}
      </div>
      {editErrors[field] && <span className="text-[9px] text-red-500 mt-0.5">{editErrors[field]}</span>}
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[#EDE8DC] bg-[#faf7f2] flex items-center justify-between shrink-0">
        <div>
          <h3 className="text-sm font-bold text-[#0A0A0A]">{deal.deal_number}</h3>
          <p className="text-[10px] text-[#888] mt-0.5">{(deal.loan_type || "").replace(/_/g, " ")} • {deal.state || "TX"}</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#EDE8DC] transition-colors" data-testid="close-deal-panel">
          <X className="h-4 w-4 text-[#666]" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {/* ── Section: Deal Summary ── */}
        <div data-testid="panel-deal-summary">
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-3">Deal Summary</h4>
          {deal.property_address && (
            <div className="flex items-start gap-2 mb-3">
              <MapPin className="h-3.5 w-3.5 text-[#999] mt-0.5 shrink-0" />
              <span className="text-xs text-[#333]">{deal.property_address}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Loan Amount", value: deal.loan_amount > 0 ? fmtAmt(deal.loan_amount) : null, accent: true },
              { label: "Purchase Price", value: deal.purchase_price > 0 ? fmtAmt(deal.purchase_price) : null },
              { label: "Est. ARV", value: deal.arv > 0 ? fmtAmt(deal.arv) : null },
              { label: "LTV", value: deal.ltv ? `${(deal.ltv * 100).toFixed(0)}%` : null },
              { label: "Borrower FICO", value: deal.fico_estimate || null },
              { label: "Match Score", value: deal.match_score > 0 ? deal.match_score : null },
            ].map((m, i) => (
              <div key={i} className="p-2.5 rounded-lg border border-[#EDE8DC] bg-[#FDFCF9]">
                <div className="text-[9px] text-[#999] uppercase tracking-wider font-semibold">{m.label}</div>
                {m.value ? (
                  <div className={`text-sm font-bold mt-0.5 ${m.accent ? "text-emerald-700" : "text-[#0A0A0A]"}`}>{m.value}</div>
                ) : (
                  <div className="text-xs italic text-[#bbb] mt-0.5">Not provided</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Section: Your Term Sheet ── */}
        {currentTs && (
          <div data-testid="panel-term-sheet">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#888]">Your Term Sheet</h4>
              {canEdit && !editing && (
                <button
                  onClick={startEditing}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 transition-colors"
                  data-testid="quick-edit-ts-btn"
                >
                  <Edit3 className="h-3 w-3" />
                  Quick Edit
                </button>
              )}
              {!canEdit && currentTs.status !== "submitted" && (
                <div className="group relative">
                  <button
                    disabled
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold text-[#aaa] bg-gray-50 border border-gray-200 cursor-not-allowed"
                    data-testid="edit-ts-disabled-btn"
                  >
                    <Edit3 className="h-3 w-3" />
                    Edit
                  </button>
                  <div className="absolute right-0 top-full mt-1 px-2 py-1 rounded-md bg-[#333] text-white text-[9px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    Term sheet locked after borrower selection. Contact Imaani Capital for modifications.
                  </div>
                </div>
              )}
            </div>

            {/* Edit Mode */}
            {editing ? (
              <div className="rounded-xl border border-purple-200 bg-purple-50/30 p-4 space-y-3" data-testid="ts-edit-form">
                <div className="grid grid-cols-2 gap-3">
                  <EditField label="Interest Rate" field="interest_rate" type="number" suffix="%" />
                  <EditField label="Points" field="points" type="number" />
                  <EditField label="Loan Amount" field="loan_amount" type="number" prefix="$" />
                  <EditField label="Term (months)" field="term_months" type="number" suffix="mo" />
                  <EditField label="Closing Days" field="closing_days" type="number" suffix="d" />
                  <div>
                    <label className="text-[10px] text-[#888] uppercase tracking-wider font-semibold">Payment Structure</label>
                    <select value={editForm.payment_structure || "interest_only"}
                      onChange={e => setEditForm(prev => ({ ...prev, payment_structure: e.target.value }))}
                      className="w-full text-xs border rounded-lg px-2 py-1.5 border-[#EDE8DC] focus:outline-none focus:ring-1 focus:ring-emerald-300 mt-0.5"
                      data-testid="edit-ts-payment-structure">
                      <option value="interest_only">Interest Only</option>
                      <option value="principal_interest">P+I (Amortizing)</option>
                    </select>
                  </div>
                </div>
                <PrepaymentPenaltyInput
                  value={editForm.prepayment_penalty_structure}
                  onChange={v => setEditForm(prev => ({ ...prev, prepayment_penalty_structure: v }))}
                  loanAmount={Number(editForm.loan_amount) || 0}
                  rate={Number(editForm.interest_rate) || 0}
                  compact
                />
                <div>
                  <label className="text-[10px] text-[#888] uppercase tracking-wider font-semibold">Conditions</label>
                  <textarea
                    value={editForm.conditions || ""}
                    onChange={e => setEditForm(prev => ({ ...prev, conditions: e.target.value }))}
                    className="w-full text-xs border border-[#EDE8DC] rounded-lg px-2 py-1.5 mt-0.5 focus:outline-none focus:ring-1 focus:ring-emerald-300 resize-none"
                    rows={2}
                    data-testid="edit-ts-conditions"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#888] uppercase tracking-wider font-semibold">Notes</label>
                  <textarea
                    value={editForm.notes || ""}
                    onChange={e => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full text-xs border border-[#EDE8DC] rounded-lg px-2 py-1.5 mt-0.5 focus:outline-none focus:ring-1 focus:ring-emerald-300 resize-none"
                    rows={2}
                    data-testid="edit-ts-notes"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={saveEdit}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 transition-colors"
                    data-testid="save-ts-edit-btn"
                  >
                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="px-3 py-2 rounded-lg text-xs font-bold text-[#666] bg-white border border-[#EDE8DC] hover:bg-gray-50 transition-colors"
                    data-testid="cancel-ts-edit-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <div className="rounded-xl border border-purple-200 bg-purple-50/30 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#0A0A0A]">{fmtAmt(currentTs.loan_amount || 0)} at {currentTs.interest_rate}%</span>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${currentTs.payment_structure === "principal_interest" ? "bg-blue-100 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                      {currentTs.payment_structure === "principal_interest" ? "P+I" : "IO"}
                    </span>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${tsStatusColor(currentTs.status)}`}>{currentTs.status === "submitted" ? "Under Review" : currentTs.status}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className="flex justify-between"><span className="text-[#888]">Points</span><span className="font-semibold text-[#333]">{currentTs.points || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-[#888]">Term</span><span className="font-semibold text-[#333]">{currentTs.term_months ? `${currentTs.term_months}mo` : "—"}</span></div>
                  <div className="flex justify-between"><span className="text-[#888]">Closing</span><span className="font-semibold text-[#333]">{currentTs.closing_days ? `${currentTs.closing_days} days` : "—"}</span></div>
                  <div className="flex justify-between"><span className="text-[#888]">Prepay Penalty</span><span className="font-semibold text-[#333]">{currentTs.prepayment_penalty || "None"}</span></div>
                </div>
                {currentTs.conditions && (
                  <div className="text-[11px]"><span className="text-[#888]">Conditions: </span><span className="text-[#333]">{currentTs.conditions}</span></div>
                )}
                {currentTs.notes && (
                  <div className="text-[11px]"><span className="text-[#888]">Notes: </span><span className="text-[#333]">{currentTs.notes}</span></div>
                )}
                <div className="text-[10px] text-[#aaa] pt-1 border-t border-purple-100">
                  Submitted {formatDate(currentTs.submitted_at)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Section: Activity Timeline ── */}
        {timeline.length > 0 && (
          <div data-testid="panel-timeline">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-3">Activity Timeline</h4>
            <div className="relative pl-4 space-y-3">
              <div className="absolute left-[7px] top-1 bottom-1 w-px bg-[#EDE8DC]" />
              {timeline.map((t, i) => (
                <div key={i} className="relative flex items-start gap-3">
                  <div className={`absolute left-[-10px] top-1.5 w-2 h-2 rounded-full border-2 ${i === 0 ? "border-emerald-500 bg-emerald-100" : "border-[#ccc] bg-white"}`} />
                  <div className="ml-2">
                    <div className="text-xs text-[#333] font-medium">{t.event}</div>
                    <div className="text-[10px] text-[#aaa]">{formatDate(t.date)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Section: Lender Brief ── */}
        {deal.lender_brief && (
          <div data-testid="panel-lender-brief">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-3">Deal Brief</h4>
            <div className="rounded-xl border border-[#E5C98B]/30 bg-amber-50/30 p-4 space-y-2">
              {deal.lender_brief.headline && (
                <p className="text-xs font-semibold text-[#333]">{deal.lender_brief.headline}</p>
              )}
              {deal.lender_brief.opportunity_summary && (
                <p className="text-[11px] text-[#555] leading-relaxed">{deal.lender_brief.opportunity_summary}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="px-5 py-3 border-t border-[#EDE8DC] bg-[#faf7f2] shrink-0">
        <button
          onClick={() => onOpenChat(deal.lead_id)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-800 transition-colors"
          data-testid="panel-message-btn"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          Message Imaani Capital Team
        </button>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   Lender Performance Card
   ═══════════════════════════════════════════════════════════════ */

function LenderPerformanceCard() {
  const [perf, setPerf] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    axios.get(`${API}/lender/performance`)
      .then(r => setPerf(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!perf) return <p className="text-sm text-[#888] text-center py-8">Unable to load performance data.</p>;

  const fmtVol = (c) => {
    const v = c / 100;
    return v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`;
  };

  const CompareArrow = ({ mine, platform }) => {
    if (!platform || platform === 0) return null;
    if (mine >= platform) return <ArrowUpRight className="h-3 w-3 text-emerald-500 inline ml-1" />;
    return <ArrowDownRight className="h-3 w-3 text-red-400 inline ml-1" />;
  };

  const metrics = [
    { label: "Deals Reviewed", value: perf.deals_reviewed ?? perf.total_matches, platform: null },
    { label: "Interest Rate", value: `${perf.interest_rate}%`, platform: perf.platform_avg_interest_rate, platformLabel: `${perf.platform_avg_interest_rate}%`, mine: perf.interest_rate },
    { label: "Term Sheet Rate", value: `${perf.term_sheet_rate}%`, platform: perf.platform_avg_term_sheet_rate, platformLabel: `${perf.platform_avg_term_sheet_rate}%`, mine: perf.term_sheet_rate },
    { label: "Funded Deals", value: perf.funded_count, platform: null },
    { label: "Avg Days to Fund", value: perf.avg_days_to_funded ? `${perf.avg_days_to_funded}d` : "--", platform: null },
    { label: "Total Volume", value: perf.total_volume_cents > 0 ? fmtVol(perf.total_volume_cents) : "--", platform: null },
  ];

  return (
    <div className="space-y-4" data-testid="lender-performance-card">
      <h2 className="text-sm font-bold text-[#0A0A0A]">Your Performance</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {metrics.map((m) => (
          <div key={m.label} className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid={`perf-metric-${m.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-[10px] font-bold text-[#888] uppercase tracking-wider mb-2">{m.label}</div>
            <div className="text-xl font-bold text-[#0A0A0A]">
              {m.value}
              {m.platform != null && <CompareArrow mine={m.mine} platform={m.platform} />}
            </div>
            {m.platformLabel && (
              <div className="text-[9px] text-[#aaa] mt-1">
                Platform avg: {m.platformLabel}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-4">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="h-4 w-4 text-emerald-600" />
          <span className="text-xs font-bold text-[#0A0A0A]">Activity Summary</span>
        </div>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-emerald-600">{perf.interests_expressed}</div>
            <div className="text-[10px] text-[#888]">Interests</div>
          </div>
          <div>
            <div className="text-lg font-bold text-purple-600">{perf.term_sheets_submitted}</div>
            <div className="text-[10px] text-[#888]">Term Sheets</div>
          </div>
          <div>
            <div className="text-lg font-bold text-[#B8943E]">{perf.funded_count}</div>
            <div className="text-[10px] text-[#888]">Funded</div>
          </div>
        </div>
      </div>
    </div>
  );
}

